'use client';

import React, { useState, useEffect } from 'react';
import { ArrowDown2, ArrowUp2 } from 'iconsax-react';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/Icons';

interface SentimentProps {
    newsSentiment?: {
        bearish?: number;
        bullish?: number;
    };
    newsId: number;
    isSingle?: boolean;
    page?: string | undefined;
    archive?: boolean | undefined;
}

const apiUrl = process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL!;

const NewsSentiment: React.FC<SentimentProps> = ({
    newsSentiment,
    newsId,
    isSingle,
    page,
    archive,
}) => {
    const [bullish, setBullish] = useState(newsSentiment?.bullish || 0);
    const [bearish, setBearish] = useState(newsSentiment?.bearish || 0);
    const [hasVoted, setHasVoted] = useState<boolean>(false); // Track if user has voted
    const [activeButton, setActiveButton] = useState<'bullish' | 'bearish' | null>(null); // Track which button is active
    const [loading, setLoading] = useState<'bullish' | 'bearish' | null>(null); // Track loading state for each button
    // Get the toast function from the hook
    const { toast } = useToast();

    useEffect(() => {
        // Check if the user has already voted by checking sessionStorage
        // const voted = sessionStorage.getItem(`voted-news-${newsId}`);
        const voteValue: 'bullish' | 'bearish' | null = sessionStorage.getItem(`vote-${newsId}`) as
            | 'bullish'
            | 'bearish'
            | null;
        if (voteValue) {
            setActiveButton(voteValue);
            setHasVoted(true); // Mark that the user has voted
        }
    }, [newsId]);

    // Function to handle vote submission
    const updateSentiment = async (type: 'bullish' | 'bearish') => {
        if (hasVoted) {
            // If the user has already voted, show a toast
            toast({
                title: 'شما قبلاً رأی داده‌اید',
                className: 'bg-error/95 text-white rounded-2xl border-none',
            });
            return; // Prevent submitting again if the user has already voted
        }

        setLoading(type); // Set loading state when button is clicked

        // Prepare the GraphQL mutation payload
        const mutation = {
            query: `
        mutation MyMutation {
          updateNewsSentiment(
            input: {
              bullish: ${bullish + (type === 'bullish' ? 1 : 0)},
              bearish: ${bearish + (type === 'bearish' ? 1 : 0)},
              id: "${newsId}",
              clientMutationId: "uniqueId"
            }
          ) {
            message
            success
          }
        }
      `,
        };

        try {
            // Send the request to the WordPress GraphQL API
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(mutation),
            });

            const result = await response.json();
            if (result.data.updateNewsSentiment.success) {
                // Update local state if the mutation was successful
                if (type === 'bullish') {
                    setBullish(bullish + 1);
                } else {
                    setBearish(bearish + 1);
                }

                // Mark that the user has voted and save it in sessionStorage
                // sessionStorage.setItem(`voted-news-${newsId}`, 'true');
                sessionStorage.setItem(`vote-${newsId}`, type);
                setHasVoted(true); // Prevent further voting
                setActiveButton(type); // Mark the clicked button as active

                // Show success toast in Farsi
                toast({
                    title: 'رأی شما با موفقیت ثبت شد!',
                    description: type === 'bullish' ? 'شما رأی صعودی دادید' : 'شما رأی نزولی دادید',
                    className:
                        'bg-white/95 dark:bg-white dark:text-primary rounded-2xl border-none',
                });
            } else {
                console.error(
                    'Failed to update sentiment:',
                    result.data.updateNewsSentiment.message,
                );
            }
        } catch (error) {
            console.error('Error updating sentiment:', error);
        } finally {
            setLoading(null); // Reset loading state after request completes
        }
    };
    return (
        <div
            className={`flex ${archive ? 'max-lg:flex-col max-lg:gap-1' : ''} gap-2 max-lg:justify-center`}
            dir={'rtl'}
        >
            {/* Bullish button */}
            <button
                className={`${isSingle === true ? 'rounded-tl-[200px] rounded-[60px] ps-2 pe-4 py-1' : 'rounded-md p-1 !border-success/50'} border flex items-center transition-all group justify-center ${
                    activeButton === 'bullish'
                        ? 'bg-success/15 dark:bg-success/30'
                        : 'border-success'
                } border:bg-success/80 hover:border-success/80`}
                onClick={() => updateSentiment('bullish')}
                disabled={loading === 'bullish'} // Disable button while loading
            >
                <span
                    className={`text-xxs pe-2 font-light text-black/80 max-lg:hidden ${isSingle || page === 'symbol' ? 'hidden' : ''} dark:text-white transition-all ${activeButton === 'bullish' && 'text-black dark:text-white'}`}
                >
                    صعودی
                </span>
                {loading === 'bullish' ? (
                    <LoadingSpinner className={`size-4 animate-spin text-black dark:text-white`} />
                ) : (
                    <div className='inline-flex items-center gap-1'>
                        <ArrowUp2 className={'fill-success'} size={14} />
                        {bullish > 0 && (
                            <span
                                className={`text-black/80 dark:text-white/80 transition-all ${activeButton === 'bullish' && 'text-black dark:text-white'} ${isSingle === false && 'text-[10px]'}`}
                            >
                                {bullish}
                            </span>
                        )}
                    </div>
                )}
            </button>

            {/* Bearish button */}
            <button
                className={`${isSingle === true ? 'rounded-br-[200px] rounded-[60px] pe-2 ps-4 py-1 ' : 'rounded-md p-1 !border-error/50'} flex items-center border transition-all group justify-center ${
                    activeButton === 'bearish' ? 'bg-error/15 dark:bg-error/30' : 'border-error'
                } hover:border-error/80`}
                onClick={() => updateSentiment('bearish')}
                disabled={loading === 'bearish'} // Disable button while loading
            >
                <span
                    className={`text-black/80 dark:text-white/80 text-xxs font-light pe-2 ${isSingle || page === 'symbol' ? 'hidden' : ''} transition-all max-lg:hidden ${activeButton === 'bearish' && 'text-black/90 dark:text-white/90'}`}
                >
                    نزولی
                </span>
                {loading === 'bearish' ? (
                    <LoadingSpinner className={`size-4 animate-spin text-black dark:text-white`} />
                ) : (
                    <div className='inline-flex items-center gap-1'>
                        <ArrowDown2 size={14} className={'fill-error'} />
                        {bearish > 0 && (
                            <span
                                className={`text-black/80 dark:text-white/80 transition-all ${activeButton === 'bearish' && 'text-black/90 dark:text-white/90'} ${isSingle === false && 'text-xxs'}`}
                            >
                                {bearish}
                            </span>
                        )}
                    </div>
                )}
            </button>
        </div>
    );
};

export default NewsSentiment;
